import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { StatusBadge } from '@/components/common/StatusBadge';
import { dashboardAPI, complianceAPI } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { useAnnouncements } from '@/hooks/dashboard/useAnnouncements';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ActivityFeed } from '@/components/common/ActivityFeed';
import { PerformanceScoreboard } from '@/components/common/PerformanceScoreboard';
import { DashboardCharts } from '@/components/common/DashboardCharts';
import { PushNotificationToggle } from '@/components/common/PushNotificationToggle';
import { WeeklyDigestSettings } from '@/components/common/WeeklyDigestSettings';
import { TopBirthdayBanner } from '@/components/dashboard/TopBirthdayBanner';
import {
  Building2, Users, Ticket, Mail, ClipboardList, ArrowRight, Clock,
  Megaphone, ShieldAlert, BarChart3, Settings,
} from 'lucide-react';
import { DashboardShell } from './DashboardShell';
import { MetricCard } from './MetricCard';
import { ActionCenter } from './ActionCenter';
import { ContractingHealth } from './ContractingHealth';
import OverdueLoansTile from '@/components/supplies/OverdueLoansTile';
import { FollowupQueueWidget } from '@/components/crm/FollowupQueueWidget';

export const KRDashboard = () => {
  const [stats, setStats] = useState({});
  const [recentRequests, setRecentRequests] = useState([]);
  const { announcements } = useAnnouncements();
  const [complianceAlerts, setComplianceAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);

  // Phase 27.7 — same-page hash navigation. React Router does not scroll
  // to `#follow-ups-panel` when the pathname doesn't change (only the
  // hash does), so the "Review follow-ups" attention card looked dead.
  // Watch the hash and scroll the target into view whenever it changes.
  const location = useLocation();
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1); // strip leading '#'
    const scroll = () => {
      const el = document.querySelector(`[data-testid="${id}"]`) || document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    // Wait a frame so late-mounted panels are in the DOM.
    const t = window.setTimeout(scroll, 150);
    return () => window.clearTimeout(t);
  }, [location.hash, loading]);

  const load = async () => {
    try {
      const [statsRes, recentRes] = await Promise.all([
        dashboardAPI.stats(),
        dashboardAPI.recentRequests(5),
      ]);
      setStats(statsRes.data);
      setRecentRequests(recentRes.data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
    try {
      const compRes = await complianceAPI.summary();
      setComplianceAlerts(compRes.data.agents_with_missing || []);
    } catch (e) {
      console.error('Failed to load compliance summary:', e);
    }
  };

  return (
    <DashboardShell
      title="Dashboard"
      description="Monitor operations, contracting activity, and team health across KR Enterprise Group."
      actions={
        <div className="flex items-center gap-3">
          <PushNotificationToggle />
        </div>
      }
    >
      <ActionCenter
        scope="admin"
        extras={{
          missingInfoCount: stats?.requests_by_status?.Missing_Info || 0,
          openTicketsCount: stats?.open_tickets || 0,
        }}
        parentLoading={loading}
      />

      <ContractingHealth />

      <TopBirthdayBanner days={14} />

      {/* KR Admin KPIs — kept to the most useful few (tickets/compliance/
          follow-ups now live in the Action Center, not as competing cards). */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <MetricCard icon={Building2} value={stats.total_agencies} label="Total Agencies" color="bg-slate-900" href="/agencies" loading={loading} />
        <MetricCard icon={Users} value={stats.total_agents} label="Total Agents" color="bg-sky-600" href="/agents" loading={loading} />
        <MetricCard icon={ClipboardList} value={stats.pending_requests} label="Pending Requests" color="bg-amber-500" href="/requests" loading={loading} />
      </div>

      {/* Phase 27.54 (Phase 3) — Equipment overdue-returns tile with deep
          link into the standalone /supplies operations app. */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <OverdueLoansTile />
      </div>

      {stats.requests_by_status && Object.keys(stats.requests_by_status).length > 0 && (
        <Card className="mb-8 animate-slideIn stagger-1">
          <CardHeader><CardTitle className="text-lg font-heading">Request Status Overview</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {Object.entries(stats.requests_by_status).map(([status, count]) => (
                <div key={status} className="p-4 rounded-lg bg-slate-50 text-center">
                  <StatusBadge status={status} className="mb-2" />
                  <p className="text-2xl font-semibold text-slate-900 mt-2">{count}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Announcements */}
      {announcements.length > 0 && (
        <Card className="mb-8 animate-slideIn border-l-4 border-l-blue-500" data-testid="admin-announcements">
          <CardHeader className="flex flex-row items-center justify-between py-3">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Megaphone size={16} className="text-blue-500" />
              Announcements
            </CardTitle>
            <Link to="/announcements"><Button variant="ghost" size="sm" className="text-xs">Manage <ArrowRight size={12} className="ml-1" /></Button></Link>
          </CardHeader>
          <CardContent className="pt-0 space-y-2">
            {announcements.map((ann) => (
              <div key={ann.announcement_id} className="p-3 bg-blue-50/50 rounded-lg border border-blue-100">
                <p className="text-sm font-medium text-slate-900">{ann.title}</p>
                <p className="text-xs text-slate-600 line-clamp-2 mt-0.5">{ann.message}</p>
                <p className="text-[10px] text-slate-400 mt-1">{formatDate(ann.publish_date)}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Compliance Alerts, Support Tickets and Follow-ups are now surfaced
          inside the Action Center above (single source of truth) — no longer
          shown here as separate competing cards. */}

      {/* Unified CRM Follow-up Queue — detailed drill-down for the follow-up
          counts shown in the Action Center. */}
      <div className="mb-8 animate-slideIn">
        <FollowupQueueWidget />
      </div>

      {/* Admin Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8 animate-slideIn stagger-1" data-testid="admin-quick-actions">
        <Link to="/reports">
          <div className="p-4 rounded-xl border border-slate-200 hover:border-sky-300 hover:bg-sky-50/50 transition-all group cursor-pointer">
            <BarChart3 size={20} className="text-sky-600 mb-2 group-hover:scale-110 transition-transform" />
            <p className="text-sm font-medium text-slate-900">Reports</p>
            <p className="text-[10px] text-slate-500">Export CSV data</p>
          </div>
        </Link>
        <Link to="/email-templates">
          <div className="p-4 rounded-xl border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/50 transition-all group cursor-pointer">
            <Mail size={20} className="text-emerald-600 mb-2 group-hover:scale-110 transition-transform" />
            <p className="text-sm font-medium text-slate-900">Email Templates</p>
            <p className="text-[10px] text-slate-500">Manage notifications</p>
          </div>
        </Link>
        <Link to="/users">
          <div className="p-4 rounded-xl border border-slate-200 hover:border-violet-300 hover:bg-violet-50/50 transition-all group cursor-pointer">
            <Settings size={20} className="text-violet-600 mb-2 group-hover:scale-110 transition-transform" />
            <p className="text-sm font-medium text-slate-900">User Management</p>
            <p className="text-[10px] text-slate-500">Roles & access</p>
          </div>
        </Link>
        <Link to="/audit-log">
          <div className="p-4 rounded-xl border border-slate-200 hover:border-amber-300 hover:bg-amber-50/50 transition-all group cursor-pointer">
            <ShieldAlert size={20} className="text-amber-600 mb-2 group-hover:scale-110 transition-transform" />
            <p className="text-sm font-medium text-slate-900">Audit Log</p>
            <p className="text-[10px] text-slate-500">Track changes</p>
          </div>
        </Link>
      </div>

      {/* Analytics Charts */}
      <DashboardCharts />

      {/* Recent Requests + Activity Feed */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {recentRequests.length > 0 && (
          <Card className="animate-slideIn stagger-2">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <Clock size={20} className="text-slate-400" />
                Recent Contracts
              </CardTitle>
              <Link to="/requests"><Button variant="outline" size="sm">View All</Button></Link>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recentRequests.map((request, index) => (
                  <Link key={request.request_id} to={`/requests/${request.request_id}`}
                    className="block p-4 rounded-lg border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-colors"
                    data-testid={`recent-request-${index}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-900">{request.agent_name}</p>
                        <p className="text-sm text-slate-500">{request.carrier_name} | {request.level_name}</p>
                      </div>
                      <div className="text-right">
                        <StatusBadge status={request.current_status} />
                        <p className="text-xs text-slate-400 mt-1">{formatDate(request.created_at)}</p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        <ActivityFeed />
      </div>

      {/* Performance Scoreboard + Weekly Digest Settings */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
        <PerformanceScoreboard />
        <WeeklyDigestSettings />
      </div>
    </DashboardShell>
  );
};

export default KRDashboard;
